import React, { useState, useRef, useEffect } from 'react';

// Cross-platform share (shared pattern across jmsgames titles). Six platforms
// use per-platform share intents; Instagram & Discord have no web share intent
// so they copy the link (the page's og:image makes the pasted link render rich).
const SHARE_URL = 'https://drugbiz.jmsgames.com';
const SHARE_TEXT =
  "Play Drug BiZ — buy low, sell high, build an empire, and don't get caught!";

const u = encodeURIComponent(SHARE_URL);
const t = encodeURIComponent(SHARE_TEXT);
const tu = encodeURIComponent(SHARE_TEXT + ' ' + SHARE_URL);

const INTENTS = {
  x: `https://twitter.com/intent/tweet?text=${t}&url=${u}`,
  facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
  reddit: `https://www.reddit.com/submit?url=${u}&title=${t}`,
  whatsapp: `https://api.whatsapp.com/send?text=${tu}`,
  telegram: `https://t.me/share/url?url=${u}&text=${t}`,
  email: `mailto:?subject=${encodeURIComponent('Drug BiZ')}&body=${tu}`,
};

const PLATFORMS = [
  { net: 'x', label: 'X', ic: 'X', bg: '#000000' },
  { net: 'facebook', label: 'Facebook', ic: 'f', bg: '#1877f2' },
  { net: 'instagram', label: 'Instagram', ic: 'I', bg: '#e4405f' },
  { net: 'reddit', label: 'Reddit', ic: 'R', bg: '#ff4500' },
  { net: 'whatsapp', label: 'WhatsApp', ic: 'W', bg: '#25d366' },
  { net: 'telegram', label: 'Telegram', ic: 'T', bg: '#229ed9' },
  { net: 'email', label: 'Email', ic: '@', bg: '#7c7c8a' },
  { net: 'discord', label: 'Discord', ic: 'D', bg: '#5865f2' },
];

function ShareMenu() {
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState('');
  const wrapRef = useRef(null);
  const toastTimer = useRef(null);

  useEffect(() => {
    function onDocClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  function showToast(msg) {
    setToast(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(''), 2600);
  }

  function share(net) {
    setOpen(false);
    if (net === 'instagram' || net === 'discord') {
      const where = net === 'instagram' ? 'Instagram' : 'Discord';
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(SHARE_URL)
          .then(() => showToast(`Link copied! Paste it in ${where}.`))
          .catch(() => showToast(`Copy this: ${SHARE_URL}`));
      } else {
        showToast(`Copy this: ${SHARE_URL}`);
      }
      return;
    }
    if (net === 'email') {
      window.location.href = INTENTS.email;
      return;
    }
    window.open(INTENTS[net], '_blank', 'noopener,noreferrer,width=620,height=640');
  }

  return (
    <div className="share-wrap" ref={wrapRef}>
      <button
        className="header-btn share-btn"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <span className="hb-icon" aria-hidden="true">📣</span>
        <span className="hb-text">Share</span>
      </button>
      {open && (
        <div className="share-menu" role="menu">
          {PLATFORMS.map((p) => (
            <button
              key={p.net}
              className="share-item"
              role="menuitem"
              onClick={() => share(p.net)}
            >
              <span className="share-ic" style={{ background: p.bg }}>
                {p.ic}
              </span>
              {p.label}
            </button>
          ))}
        </div>
      )}
      {toast && (
        <div className="share-toast" role="status">
          {toast}
        </div>
      )}
    </div>
  );
}

export default ShareMenu;
